import { useState } from 'react';
import { Spinner, Badge, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { WorkOrderStatusBadge } from './Common';
import { formatCreatedDate, getWorkTypeBadgeColor } from '../constants/utils';

/**
 * Maps a work order's workType to a hex color for calendar event pills.
 * Uses Arcmedix design tokens from _variables.scss.
 */
const getEventHexColor = (event) => {
  switch (event.workType?.toUpperCase()) {
    case 'REACTIVE': return '#3980c0';      // $primary
    case 'PREVENTIVE': return '#fc931d';    // $warning ($yellow from _variables.scss)
    default: return '#6c757d';              // $secondary
  }
};

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  return { daysInMonth, startingDayOfWeek, year, month };
};

const getEventsForDate = (allEvents, date) => {
  return allEvents.filter(event => {
    const eventDate = new Date(event.eventDate);
    return eventDate.getDate() === date.getDate() &&
           eventDate.getMonth() === date.getMonth() &&
           eventDate.getFullYear() === date.getFullYear();
  });
};

/**
 * Reusable calendar grid component for displaying work order events.
 * Does NOT fetch data — the parent supplies events via props.
 *
 * Props:
 * - allEvents: Array of unified event objects with { id, type, eventTitle, eventDate, status, ... }
 * - loading: boolean
 * - currentDate: Date object for current month
 * - setCurrentDate: function to change month
 * - onEventClick: function(event) called when user clicks an event
 * - t: i18next translation function
 */
const WorkOrderCalendarGrid = ({ allEvents, loading, currentDate, setCurrentDate, onEventClick, t }) => {
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const monthNames = [
    t("shared.month_january", "January"),
    t("shared.month_february", "February"),
    t("shared.month_march", "March"),
    t("shared.month_april", "April"),
    t("shared.month_may", "May"),
    t("shared.month_june", "June"),
    t("shared.month_july", "July"),
    t("shared.month_august", "August"),
    t("shared.month_september", "September"),
    t("shared.month_october", "October"),
    t("shared.month_november", "November"),
    t("shared.month_december", "December")
  ];

  const dayNames = [
    t("shared.day_sun", "SUN"),
    t("shared.day_mon", "MON"),
    t("shared.day_tue", "TUE"),
    t("shared.day_wed", "WED"),
    t("shared.day_thu", "THU"),
    t("shared.day_fri", "FRI"),
    t("shared.day_sat", "SAT")
  ];

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleShowMore = (date, events) => {
    setSelectedDate(date);
    setSelectedDayEvents(events);
    setShowMoreModal(true);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" size="lg" />
        <p className="text-muted mt-3">{t("business.calendar.loading_calendar", "Loading calendar...")}</p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="google-calendar-container"
      >
        {/* Calendar Header */}
        <div className="calendar-header">
          <div className="d-flex align-items-center gap-3">
            <Button
              color="light"
              className="rounded-circle border-0 shadow-sm"
              style={{ width: '40px', height: '40px', padding: 0 }}
              onClick={goToToday}
            >
              <FeatherIcon icon="calendar" size={18} />
            </Button>
            <h2 className="calendar-month-title mb-0">{monthNames[month]} {year}</h2>
            <Badge color="info" className="ms-2">
              {allEvents.length} {t("shared.events", "events")}
            </Badge>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Button color="light" size="sm" className="border-0" onClick={previousMonth}>
              <FeatherIcon icon="chevron-left" size={20} />
            </Button>
            <Button color="light" size="sm" className="border-0" onClick={nextMonth}>
              <FeatherIcon icon="chevron-right" size={20} />
            </Button>
            <Button color="primary" size="sm" className="ms-3" onClick={goToToday}>
              {t("shared.today", "Today")}
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="calendar-legend">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="text-muted small fw-medium">{t("shared.legend", "Legend:")}</span>
            {[
              { workType: 'REACTIVE', label: t("shared.reactive", "Reactive") },
              { workType: 'PREVENTIVE', label: t("shared.preventive", "Preventive") },
            ].map(item => (
              <div key={item.workType} className="d-flex align-items-center gap-2">
                <span className="legend-dot" style={{ backgroundColor: getEventHexColor(item) }}></span>
                <span className="legend-label">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="google-calendar-grid">
          {/* Day headers */}
          <div className="calendar-weekdays">
            {dayNames.map((day, index) => (
              <div key={index} className="calendar-weekday">{day}</div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="calendar-days-grid">
            {[...Array(startingDayOfWeek)].map((_, index) => (
              <div key={`empty-${index}`} className="calendar-day empty-day"></div>
            ))}

            {[...Array(daysInMonth)].map((_, index) => {
              const day = index + 1;
              const date = new Date(year, month, day);
              const dayEvents = getEventsForDate(allEvents, date);
              const isToday = new Date().toDateString() === date.toDateString();

              return (
                <div key={day} className={`calendar-day ${isToday ? 'today' : ''}`}>
                  <div className={`calendar-day-number ${isToday ? 'today-number' : ''}`}>
                    {day}
                  </div>
                  <div className="calendar-day-events">
                    {dayEvents.slice(0, 3).map(event => {
                      const eventColor = getEventHexColor(event);
                      return (
                        <div
                          key={event.id}
                          className="calendar-event"
                          style={{
                            backgroundColor: eventColor,
                            borderLeft: `4px solid ${eventColor}`,
                            cursor: 'pointer',
                            position: 'relative'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick(event);
                          }}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltipPosition({
                              x: rect.left + rect.width / 2,
                              y: rect.top - 10
                            });
                            setHoveredEvent(event);
                          }}
                          onMouseLeave={() => setHoveredEvent(null)}
                        >
                          <span className="event-title">{event.eventTitle}</span>
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div
                        className="calendar-event-more"
                        onClick={() => handleShowMore(date, dayEvents)}
                      >
                        +{dayEvents.length - 3} {t("shared.more", "more")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Show More Events Modal */}
      <Modal isOpen={showMoreModal} toggle={() => setShowMoreModal(false)} size="lg">
        <ModalHeader toggle={() => setShowMoreModal(false)}>
          {t("business.calendar.events_on", "Events on")} {selectedDate ? formatCreatedDate(selectedDate) : ''}
        </ModalHeader>
        <ModalBody>
          <div className="events-list">
            {selectedDayEvents.map((event) => {
              const eventColor = getEventHexColor(event);
              return (
                <div
                  key={event.id}
                  className="event-detail-item"
                  style={{
                    borderLeft: `4px solid ${eventColor}`,
                    padding: '12px 16px',
                    marginBottom: '8px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setShowMoreModal(false);
                    onEventClick(event);
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Badge color={getWorkTypeBadgeColor(event.workType)}>
                          {event.workType || 'REACTIVE'}
                        </Badge>
                        {event.status && (
                          <WorkOrderStatusBadge status={event.status} />
                        )}
                      </div>
                      <h6 className="mb-1">{event.eventTitle}</h6>
                      {event.description && (
                        <p className="text-muted small mb-1">{event.description}</p>
                      )}
                      <small className="text-muted">
                        {formatCreatedDate(event.eventDate)}
                      </small>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ModalBody>
      </Modal>

      {/* Hover Tooltip */}
      <AnimatePresence>
      {hoveredEvent && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 10 }}
          transition={{ duration: 0.15 }}
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999,
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.1)',
              padding: '16px',
              minWidth: '280px',
              maxWidth: '350px',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Header with Type Badge */}
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Badge color={getWorkTypeBadgeColor(hoveredEvent.workType)}>
                {hoveredEvent.workType || 'REACTIVE'}
              </Badge>
              {hoveredEvent.status && (
                <WorkOrderStatusBadge status={hoveredEvent.status} />
              )}
            </div>

            {/* Title */}
            <h6 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#1a1a1a' }}>
              {hoveredEvent.eventTitle || hoveredEvent.title || t("shared.untitled", "Untitled")}
            </h6>

            {/* Details Grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Request ID */}
              {hoveredEvent.requestId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FeatherIcon icon="hash" size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#4b5563', fontFamily: 'monospace' }}>
                    {hoveredEvent.requestId}
                  </span>
                </div>
              )}

              {/* Ticket Number */}
              {hoveredEvent.ticketNumber && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FeatherIcon icon="file-text" size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#4b5563', fontFamily: 'monospace' }}>
                    {hoveredEvent.ticketNumber}
                  </span>
                </div>
              )}

              {/* Asset */}
              {hoveredEvent.asset?.assetName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FeatherIcon icon="package" size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#4b5563' }}>
                    {hoveredEvent.asset.assetName}
                  </span>
                </div>
              )}

              {/* Assigned Technicians (array) */}
              {hoveredEvent.assignedTechnician && Array.isArray(hoveredEvent.assignedTechnician) && hoveredEvent.assignedTechnician.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FeatherIcon icon="users" size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {hoveredEvent.assignedTechnician.slice(0, 2).map((tech, idx) => (
                      <span key={idx} style={{ fontSize: '11px', padding: '2px 6px', backgroundColor: '#e5e7eb', borderRadius: '4px', color: '#374151' }}>
                        {tech.name}
                      </span>
                    ))}
                    {hoveredEvent.assignedTechnician.length > 2 && (
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>
                        +{hoveredEvent.assignedTechnician.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Date */}
              {hoveredEvent.eventDate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FeatherIcon icon="calendar" size={14} style={{ color: '#6b7280', flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', color: '#4b5563' }}>
                    {formatCreatedDate(hoveredEvent.eventDate)}
                  </span>
                </div>
              )}

              {/* Description */}
              {hoveredEvent.description && (
                <div style={{ marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                  <p style={{
                    fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: '1.5',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                  }}>
                    {hoveredEvent.description}
                  </p>
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
              {t("business.calendar.click_full_details", "Click to view full details")}
            </div>

            {/* Tooltip Arrow */}
            <div style={{
              position: 'absolute', bottom: '-6px', left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
              borderTop: '6px solid white', filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
            }} />
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </>
  );
};

export default WorkOrderCalendarGrid;
